/**
 * L'ENVOI SANS BLOCAGE (retouches du 06/10/2026) : le composeur se vide et
 * garde le focus des qu'on envoie ; les messages partent EN ARRIERE-PLAN, un
 * par un, dans l'ordre ou ils ont ete ecrits.
 *
 * - ORDRE : une seule file, un envoi a la fois ; le suivant attend le
 *   precedent.
 * - ECHEC : l'envoi qui echoue ET tous ceux qui attendaient derriere lui sont
 *   rendus a `surEchec`, dans l'ordre : on ne publie jamais un message 3 quand
 *   le 2 n'est pas parti. L'ecran les remet dans le brouillon
 *   (`brouillonApresEchec`) et dit pourquoi.
 * - DOUBLE ENVOI : la file ne deduplique pas ; c'est le composeur qui vide le
 *   brouillon AVANT d'appeler `ajouter` (un second Entree trouve un champ vide).
 *
 * Pur, sans React : teste par `packages/ui/tests/file-d-envoi.test.mjs`.
 */
export interface EnvoiEnAttente<C = string> {
  texte: string;
  /** Ou le texte doit revenir en cas d'echec (un canal, un fil). */
  cible: C;
}

export interface FileDEnvoi<C = string> {
  /** Met un envoi dans la file et rend la main tout de suite. */
  ajouter(texte: string, cible: C, envoi: () => Promise<unknown>): void;
  /** Les envois pas encore confirmes, celui en vol compris. */
  readonly enAttente: number;
}

export function creerFileDEnvoi<C = string>(options: {
  surEchec: (rendus: EnvoiEnAttente<C>[], cause: unknown) => void;
  surEnvoye?: (envoi: EnvoiEnAttente<C>) => void;
}): FileDEnvoi<C> {
  const attente: Array<EnvoiEnAttente<C> & { envoi: () => Promise<unknown> }> = [];
  let enCours = false;

  async function vider(): Promise<void> {
    if (enCours) return;
    enCours = true;
    try {
      while (attente.length > 0) {
        const tache = attente[0]!;
        try {
          await tache.envoi();
          attente.shift();
          options.surEnvoye?.({ texte: tache.texte, cible: tache.cible });
        } catch (cause) {
          const rendus = attente.splice(0).map(({ texte, cible }) => ({ texte, cible }));
          options.surEchec(rendus, cause);
        }
      }
    } finally {
      enCours = false;
    }
  }

  return {
    ajouter(texte, cible, envoi) {
      attente.push({ texte, cible, envoi });
      void vider();
    },
    get enAttente() {
      return attente.length;
    },
  };
}

/** Le brouillon apres un echec : les textes rendus d'abord (ils ont ete ecrits avant), puis ce qu'on tapait. */
export function brouillonApresEchec(rendus: readonly string[], courant: string): string {
  return [...rendus, courant].filter((texte) => texte.trim() !== '').join('\n\n');
}

/** La phrase d'erreur d'un envoi rendu au brouillon (G16 : on dit pourquoi). */
export function messageDEchecDEnvoi(cause: unknown, rendus: number): string {
  const raison = raisonLisible(cause);
  return `${rendus > 1 ? `${rendus} messages non envoyés, remis` : 'Message non envoyé, remis'} dans le brouillon. ${raison}`;
}

/**
 * LA RAISON D'UN ECHEC, LISIBLE (V3-7 du 07/10/2026) : une erreur technique du
 * serveur (« PERMISSION_DENIED: evaluation error at L2266... ») ne s'affiche
 * jamais telle quelle sous la saisie. Une erreur du socle redigee en francais
 * (chatter eteint, canal en double) garde sa phrase. Exporte pour le test.
 */
export function raisonLisible(cause: unknown): string {
  const code = typeof (cause as { code?: unknown } | null)?.code === 'string' ? (cause as { code: string }).code : '';
  const texte = cause instanceof Error ? cause.message : '';
  const technique = code !== '' || /PERMISSION_DENIED|permission-denied|evaluation error|FirebaseError|\[code=|expressions to evaluate/i.test(texte);
  if (!technique) return texte.trim() !== '' ? texte : 'Réessayez.';
  if (/permission-denied|PERMISSION_DENIED/.test(`${code} ${texte}`)) return 'Le serveur a refusé l’envoi. Réessayez ; si cela continue, prévenez votre administrateur.';
  if (/unavailable|deadline-exceeded|network|offline/i.test(`${code} ${texte}`)) return 'La connexion au serveur a été interrompue. Réessayez.';
  return 'Le serveur n’a pas pu enregistrer le message. Réessayez.';
}
